<script setup lang="ts">
/**
 * A compact button in the chrome of a dense grid — a spreadsheet's filter
 * arrow in a header cell, the 1/2/3 boxes of an outline gutter. It is smaller
 * than any NxButton size, because it lives inside a cell or a gutter lane.
 *
 * The grid's layout owns its box: the caller sets position, `width` and
 * `height` (the cell's or the lane's measure) on the element; without them it
 * is a square of the extra-small icon size. The content is a short glyph or a
 * digit in the default slot. `active` shows the brand fill — a filter that is
 * set — without making it a toggle (no `aria-pressed`); name the state in the
 * `ariaLabel`.
 */
withDefaults(
  defineProps<{
    active?: boolean;
    disabled?: boolean;
    title?: string;
    ariaLabel?: string;
  }>(),
  { active: false, disabled: false, title: undefined, ariaLabel: undefined }
);
</script>

<template>
  <button
    type="button"
    class="nx-grid-button"
    :class="{ 'is-active': active }"
    :disabled="disabled"
    :title="title"
    :aria-label="ariaLabel ?? title"
  >
    <slot />
  </button>
</template>

<style scoped>
.nx-grid-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--nxt-icon-size-xs);
  height: var(--nxt-icon-size-xs);
  padding: 0;
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  line-height: 1;
  cursor: pointer;
}
.nx-grid-button:hover:not(:disabled) {
  border-color: var(--nxt-brand-bg);
  color: var(--nxt-brand-bg);
}
.nx-grid-button:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-grid-button.is-active,
.nx-grid-button.is-active:hover:not(:disabled) {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
.nx-grid-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
